// OnboardingChecker.tsx
// Decide si abrir el asistente de primeros pasos (OnboardingWizard).
//
// Oct-2026: antes tapaba el panel con "Preparando tu cuenta..." a CUALQUIERA
// que entrara, aunque ya lo hubiera terminado hacía meses: arrancaba en
// "verificando" y esperaba un segundo fijo antes de mirar nada. Ahora no
// muestra nada mientras se fija:
//  - recién registrado (lo marca Register): se abre al toque, sin ir a la API;
//  - ya lo terminó en este navegador: nada;
//  - si no, pregunta en silencio si ya tiene servicios: con 0 se abre, con
//    alguno se da por terminado.
// "Lo hago después" vale para esta sesión: si sigue sin servicios, la
// próxima vez se vuelve a abrir (sin servicio su página no recibe turnos).
import React, { useEffect, useState } from 'react';
import OnboardingWizard from './OnboardingWizard';
import { authFetch } from '../services/api';

/** Marca que deja Register al crear una cuenta de profesional (sessionStorage) */
export const RECIEN_REGISTRADO_KEY = 'consultar_recien_registrado';

interface OnboardingCheckerProps {
  userId: number;
  /** Slug público, para que el asistente entregue el link con nombre. */
  slug?: string | null;
  userName: string;
  userType: 'belleza' | 'salud';
  especialidad: string;
  onComplete: () => void;
  onSkip: () => void;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://api.consultarapp.com';

const leer = (almacen: Storage, clave: string) => {
  try { return almacen.getItem(clave); } catch { return null; }
};
const guardar = (almacen: Storage, clave: string, valor: string) => {
  try { almacen.setItem(clave, valor); } catch { /* modo privado: no pasa nada */ }
};

const OnboardingChecker: React.FC<OnboardingCheckerProps> = ({
  userId,
  slug,
  userName,
  userType,
  especialidad,
  onComplete,
  onSkip
}) => {
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    let cancelado = false;

    // Puerta para verlo a pedido: ?onboarding=1 en la URL del panel. Abrirlo
    // no toca datos; completarlo sí (crea el servicio y pisa los horarios):
    // usarlo con una cuenta de prueba.
    if (new URLSearchParams(window.location.search).get('onboarding') === '1') {
      setShowWizard(true);
      return;
    }

    if (leer(localStorage, `onboarding_completed_${userId}`)) return;
    if (leer(sessionStorage, `onboarding_skipped_${userId}`)) return;

    if (leer(sessionStorage, RECIEN_REGISTRADO_KEY) === String(userId)) {
      try { sessionStorage.removeItem(RECIEN_REGISTRADO_KEY); } catch { /* noop */ }
      setShowWizard(true);
      return;
    }

    (async () => {
      // Hasta 3 intentos: el token recién guardado o un servidor dormido
      // pueden fallar la primera vez.
      for (let intento = 0; intento < 3 && !cancelado; intento++) {
        try {
          const r = await authFetch(`${API_URL}/api/services/professional/${userId}/services`);
          if (r.ok) {
            const data = await r.json();
            if (cancelado) return;
            if ((data.services || []).length === 0) setShowWizard(true);
            else guardar(localStorage, `onboarding_completed_${userId}`, 'true');
            return;
          }
          if (r.status !== 401) return; // otro error: se vuelve a mirar la próxima vez
        } catch {
          // sin conexión: reintenta
        }
        await new Promise((res) => setTimeout(res, 1500));
      }
    })();

    return () => { cancelado = true; };
  }, [userId]);

  const handleComplete = () => {
    guardar(localStorage, `onboarding_completed_${userId}`, 'true');
    setShowWizard(false);
    onComplete();
  };

  const handleSkip = () => {
    guardar(sessionStorage, `onboarding_skipped_${userId}`, 'true');
    setShowWizard(false);
    onSkip();
  };

  return showWizard ? (
    <OnboardingWizard
      userId={userId}
      slug={slug}
      userName={userName}
      userType={userType}
      especialidad={especialidad}
      onComplete={handleComplete}
      onSkip={handleSkip}
    />
  ) : null;
};

export default OnboardingChecker;
